import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Pause, Play, SkipForward, SkipBack, Shuffle, ListOrdered, Sparkles, Eye, EyeOff, PartyPopper, Home, Headphones, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, playWordTwice, spellOut, preloadLetters, interruptCurrentAudio, pauseCurrentAudio, resumeCurrentAudio, unlockAudioPlayback } from "../lib/api";
import { shuffle } from "../lib/shuffle";

// Build the playback queue. If `random` is on, the words are shuffled.
// If we have missed words, they are inserted twice as often so reviewing
// is automatic.
function buildQueue(words, missed, random) {
  const missedSet = new Set((missed || []).map((w) => w.toLowerCase()));
  const weighted = [];
  for (const w of words) {
    weighted.push(w);
    if (missedSet.has(w.toLowerCase())) weighted.push(w); // 2x weight for missed
  }
  return random ? shuffle(weighted) : weighted;
}

const GAP_OPTIONS = [
  { label: "Quick", value: 600 },
  { label: "Steady", value: 1200 },
  { label: "Relaxed", value: 2200 },
];

// Listen-mode setup options. Each duration is the number of seconds the
// session runs before auto-stopping; null means "play through the full
// queue once" (existing behaviour from before this iteration).
const DURATION_OPTIONS = [
  { id: "5", label: "5 minutes", seconds: 5 * 60 },
  { id: "10", label: "10 minutes", seconds: 10 * 60 },
  { id: "15", label: "15 minutes", seconds: 15 * 60 },
  { id: "all", label: "All Words", seconds: null },
];

export default function Listen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [random, setRandom] = useState(true);
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | hearing | spelling | repeat | gap | done
  const [paused, setPaused] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [spellingIdx, setSpellingIdx] = useState(null);
  const [gapMs, setGapMs] = useState(1200);
  const [missedWords, setMissedWords] = useState([]);
  // Setup state — null while we're showing the picker, then the chosen
  // option's seconds (number) or 'all' (string) once started.
  const [chosen, setChosen] = useState(null);
  // Whether the current session is a focused-review of missed words.
  const [focused, setFocused] = useState(false);
  // Remaining seconds for timed sessions (null when playing 'all').
  const [remaining, setRemaining] = useState(null);

  // Refs that the run-loop reads on every tick — avoids stale-closure bugs
  // and lets us mutate behaviour mid-cycle without restarting the loop.
  const pausedRef = useRef(paused);
  const skipRef = useRef(0); // -1 prev, +1 next, 0 stay
  const stopRef = useRef(false);
  const idxRef = useRef(0);
  const gapRef = useRef(gapMs);
  // Pause/resume the actual playing audio in addition to freezing the loop.
  useEffect(() => {
    pausedRef.current = paused;
    if (paused) pauseCurrentAudio();
    else resumeCurrentAudio();
  }, [paused]);
  useEffect(() => { idxRef.current = idx; }, [idx]);
  useEffect(() => { gapRef.current = gapMs; }, [gapMs]);

  // Wait helper that wakes early if user pauses, skips, or stops.
  const sleepInterruptible = async (ms) => {
    const start = Date.now();
    while (Date.now() - start < ms) {
      if (stopRef.current || skipRef.current !== 0) return;
      // While paused, don't tick down — extend wait until resumed.
      if (pausedRef.current) {
        await new Promise((r) => setTimeout(r, 80));
        continue;
      }
      await new Promise((r) => setTimeout(r, Math.min(80, ms)));
    }
  };
  const waitWhilePaused = async () => {
    while (pausedRef.current && !stopRef.current && skipRef.current === 0) {
      await new Promise((r) => setTimeout(r, 80));
    }
  };

  useEffect(() => { preloadLetters(); }, []);

  // Prime the shared HTMLAudioElement on mount and on the first user
  // pointer/key event, so subsequent auto-play of words 2+ isn't blocked
  // by browser autoplay policies (which normally require a user gesture
  // within ~5 seconds of each play()).
  useEffect(() => {
    unlockAudioPlayback();
    const onGesture = () => { unlockAudioPlayback(); };
    window.addEventListener("pointerdown", onGesture, { once: true });
    window.addEventListener("keydown", onGesture, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
    };
  }, []);

  // Load the wordlist + missed words. We do NOT auto-build the queue any
  // more — the kid picks a duration / focused-review option first on the
  // setup screen, then `startSession()` builds the queue.
  useEffect(() => {
    (async () => {
      try {
        const [w, m] = await Promise.all([
          api.get(`/wordlists/${id}`),
          api.get(`/wordlists/${id}/missed-words`).catch(() => ({ data: { words: [] } })),
        ]);
        setList(w.data);
        setMissedWords(m.data.words || []);
      } catch { toast.error("List not found"); navigate("/wordlists"); }
    })();
  }, [id, navigate]);

  // Build (or rebuild) the queue once the kid has chosen a duration.
  // For focused review, the queue is the missed-words list directly so
  // every entry is a word they got wrong recently.
  const startSession = async (option) => {
    if (!list) return;
    // iOS: unlock audio inside this exact user-gesture handler so the
    // run-loop's auto-play of word 1 isn't blocked by Safari's autoplay
    // policy. Awaiting here keeps us inside the activation window.
    try { unlockAudioPlayback(); } catch { /* ignore */ }
    if (option.id === "focused") {
      if (!missedWords.length) return;
      setFocused(true);
      setQueue(shuffle([...missedWords]));
    } else {
      setFocused(false);
      setQueue(buildQueue(list.words || [], missedWords, random));
    }
    setIdx(0);
    setPhase("idle");
    setPaused(false);
    setChosen(option);
    setRemaining(option.seconds || null);
  };

  // Countdown timer. Decrements `remaining` once per second while playback
  // is running and not paused; on hitting 0 it stops the loop and shows
  // the done screen.
  useEffect(() => {
    if (remaining === null || remaining <= 0) return;
    if (paused) return;
    if (phase === "done") return;
    const t = setTimeout(() => {
      setRemaining((r) => {
        if (r === null) return null;
        if (r <= 1) {
          stopRef.current = true;
          interruptCurrentAudio();
          setPhase("done");
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearTimeout(t);
  }, [remaining, paused, phase]);

  // Rebuild queue when the user toggles random/in-order — only if a
  // session has been started (no point rebuilding while they're still on
  // the setup screen).
  useEffect(() => {
    if (!list || !chosen || focused) return;
    setQueue(buildQueue(list.words || [], missedWords, random));
    setIdx(0);
  }, [random]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop playback when the user navigates away.
  useEffect(() => {
    return () => { stopRef.current = true; interruptCurrentAudio(); };
  }, []);

  const word = queue[idx];

  // The run loop. One pass per "playback session" — restarts whenever the
  // queue changes (e.g. after toggling shuffle).
  //
  // The loop owns its own `cursor` (local) so word-to-word advancement is
  // deterministic and immune to React's async state batching. We mirror the
  // cursor into React state via setIdx so the UI updates, but we never
  // depend on idxRef inside the loop body for the word being played —
  // setIdx commits AFTER the next iteration starts, so reading idxRef there
  // would replay the previous word.
  useEffect(() => {
    if (!queue.length) return;
    stopRef.current = false;
    skipRef.current = 0;
    let cancelled = false;
    let cursor = 0;
    setIdx(0);
    (async () => {
      while (!stopRef.current && !cancelled) {
        if (cursor >= queue.length) {
          setPhase("done");
          return;
        }
        if (cursor < 0) cursor = 0;
        // Sync UI to the cursor on entry (handles skip-prev clamping too).
        setIdx(cursor);
        const w = queue[cursor];
        setRevealed(false);
        setSpellingIdx(null);
        await waitWhilePaused();
        if (stopRef.current || cancelled) return;
        // If a skip was queued before we even started this word, honor it.
        if (skipRef.current !== 0) {
          cursor = Math.max(0, Math.min(queue.length - 1, cursor + skipRef.current));
          skipRef.current = 0;
          continue;
        }

        // Phase 1: hear word twice (two voices, like Practice)
        setPhase("hearing");
        try { await playWordTwice(w, 450, { shouldStop: () => stopRef.current || skipRef.current !== 0 }); } catch { /* ignore */ }
        if (stopRef.current || cancelled) return;
        if (skipRef.current !== 0) {
          cursor = Math.max(0, Math.min(queue.length - 1, cursor + skipRef.current));
          skipRef.current = 0;
          continue;
        }
        await waitWhilePaused();
        await sleepInterruptible(350);
        if (skipRef.current !== 0) {
          cursor = Math.max(0, Math.min(queue.length - 1, cursor + skipRef.current));
          skipRef.current = 0;
          continue;
        }

        // Phase 2: spell letters out loud, with letter highlighting.
        // Skip the trailing full-word repeat — Phase 3 below plays the word
        // in two voices, so adding it here would produce a duplicate
        // female-voice playback right before the female-then-male of Phase 3.
        setPhase("spelling");
        setRevealed(true); // show letters as they sound
        try {
          await spellOut(w, {
            onLetter: (li) => setSpellingIdx(li >= 0 ? li : null),
            shouldStop: () => stopRef.current || skipRef.current !== 0,
            skipFinalWord: true,
          });
        } catch { /* ignore */ }
        setSpellingIdx(null);
        if (stopRef.current || cancelled) return;
        if (skipRef.current !== 0) {
          cursor = Math.max(0, Math.min(queue.length - 1, cursor + skipRef.current));
          skipRef.current = 0;
          continue;
        }
        await waitWhilePaused();
        await sleepInterruptible(300);
        if (skipRef.current !== 0) {
          cursor = Math.max(0, Math.min(queue.length - 1, cursor + skipRef.current));
          skipRef.current = 0;
          continue;
        }

        // Phase 3: hear the word one more time
        setPhase("repeat");
        try { await playWordTwice(w, 380, { shouldStop: () => stopRef.current || skipRef.current !== 0 }); } catch { /* ignore */ }
        if (stopRef.current || cancelled) return;
        if (skipRef.current !== 0) {
          cursor = Math.max(0, Math.min(queue.length - 1, cursor + skipRef.current));
          skipRef.current = 0;
          continue;
        }

        // Phase 4: gap before next word
        setPhase("gap");
        await sleepInterruptible(gapRef.current);
        if (stopRef.current || cancelled) return;
        if (skipRef.current !== 0) {
          cursor = Math.max(0, Math.min(queue.length - 1, cursor + skipRef.current));
          skipRef.current = 0;
          continue;
        }

        // Move on. If this was the last word, show the celebration screen.
        if (cursor >= queue.length - 1) {
          setPhase("done");
          return;
        }
        cursor += 1;
      }
    })();
    return () => { cancelled = true; };
  }, [queue]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePause = () => setPaused((p) => !p);
  const skipNext = () => {
    skipRef.current = 1;
    setPaused(false);
    interruptCurrentAudio();
    // If we're on the last word, jump straight to the done screen instead
    // of clamping at the same index.
    if (idxRef.current >= queue.length - 1) {
      stopRef.current = true;
      setPhase("done");
    }
  };
  const skipPrev = () => {
    skipRef.current = -1;
    setPaused(false);
    interruptCurrentAudio();
  };
  const restart = () => {
    stopRef.current = true;
    interruptCurrentAudio();
    setTimeout(() => {
      stopRef.current = false;
      skipRef.current = 0;
      // Rebuild the queue using the same option the kid originally picked
      // — focused review stays focused, timed sessions reset their timer.
      if (focused) {
        setQueue(shuffle([...missedWords]));
      } else {
        setQueue(buildQueue(list.words || [], missedWords, random));
      }
      setIdx(0);
      setPhase("idle");
      setPaused(false);
      setRemaining(chosen?.seconds || null);
    }, 80);
  };

  const goToSetup = () => {
    stopRef.current = true;
    interruptCurrentAudio();
    setChosen(null);
    setFocused(false);
    setQueue([]);
    setRemaining(null);
    setPhase("idle");
  };

  if (!list) {
    return <div className="card-soft text-center py-12 text-slate-500">Loading…</div>;
  }

  // Setup screen — picker for duration + focused-review, mirroring the
  // Take-a-Test setup so the two modes feel consistent.
  if (!chosen) {
    return (
      <div className="space-y-6" data-testid="listen-setup">
        <div>
          <div className="text-sm font-bold text-[#10B981] uppercase tracking-widest">Listen mode</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1E1B4B]">{list.name}</h1>
        </div>
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.35 }}
          className="card-soft text-center py-10"
        >
          <div className="inline-flex w-16 h-16 rounded-3xl bg-[#10B981] items-center justify-center shadow-[0_6px_0_#047857]">
            <Headphones className="w-8 h-8 text-white" strokeWidth={3} />
          </div>
          <h2 className="font-display text-3xl font-bold text-[#1E1B4B] mt-4">How long today?</h2>
          <p className="text-slate-600 mt-1 font-semibold">Pick a session length</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3" data-testid="listen-duration-options">
            {DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => startSession(opt)}
                data-testid={`listen-duration-${opt.id}`}
                className={opt.id === "all" ? "btn-clay min-w-[140px]" : "btn-clay-blue min-w-[140px]"}
              >
                {opt.id !== "all" && <Clock className="w-4 h-4" strokeWidth={3} />} {opt.label}
              </button>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t-2 border-dashed border-blue-100">
            <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Focused review</p>
            {missedWords.length > 0 ? (
              <>
                <button
                  onClick={() => startSession({ id: "focused", label: "Focused Review", seconds: null })}
                  data-testid="listen-focused-btn"
                  className="btn-clay-yellow mt-2 min-w-[260px]"
                >
                  <RefreshCw className="w-5 h-5" strokeWidth={3} />
                  Hear {missedWords.length} missed word{missedWords.length === 1 ? "" : "s"}
                </button>
                <p className="text-xs text-slate-500 mt-2">
                  Listen mode using only the words you got wrong recently.
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-500 mt-2" data-testid="listen-no-missed-msg">
                No missed words yet — pick a duration above to start a normal session.
              </p>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  if (!queue.length) {
    return <div className="card-soft text-center py-12 text-slate-500">Loading…</div>;
  }

  if (phase === "done") {
    const finishedByTimer = remaining === 0 && (chosen?.seconds || 0) > 0;
    return (
      <motion.div
        className="card-soft text-center py-16"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        data-testid="listen-done-card"
      >
        <PartyPopper className="w-14 h-14 mx-auto text-[#FBBF24]" strokeWidth={3} />
        <h2 className="font-display text-4xl font-bold text-[#1E1B4B] mt-4">
          {finishedByTimer ? "Time's up!" : "All done!"}
        </h2>
        <p className="mt-2 text-slate-600 font-semibold">
          {finishedByTimer
            ? `You listened for ${chosen.label.toLowerCase()}. Great work!`
            : `You listened to all ${queue.length} words. Buzz buzz!`}
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <button onClick={restart} className="btn-clay-blue" data-testid="listen-restart-btn">
            <Play className="w-5 h-5" strokeWidth={3} /> Listen again
          </button>
          <button onClick={goToSetup} className="btn-clay-ghost" data-testid="listen-change-session-btn">
            <Headphones className="w-5 h-5" strokeWidth={3} /> Change session
          </button>
          <button onClick={() => navigate(`/wordlists/${list.id}`)} className="btn-clay-ghost" data-testid="listen-backtolist-btn">
            <Home className="w-5 h-5" strokeWidth={3} /> Back to list
          </button>
        </div>
      </motion.div>
    );
  }

  const phaseLabel = {
    idle: "Getting ready…",
    hearing: "Listen to the word",
    spelling: "Now spelling it out",
    repeat: "And once more",
    gap: "Get ready for the next one",
  }[phase] || "";

  return (
    <div className="space-y-6" data-testid="listen-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-bold text-[#10B981] uppercase tracking-widest">Listen mode</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1E1B4B]">{list.name}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {focused && (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 font-bold text-xs uppercase tracking-widest" data-testid="listen-focused-chip">
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={3} /> Focused review
            </span>
          )}
          {remaining !== null && (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1E1B4B] text-white font-bold text-xs tabular-nums" data-testid="listen-timer">
              <Clock className="w-3.5 h-3.5" strokeWidth={3} /> {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}
            </span>
          )}
          <button
            onClick={() => setRandom((r) => !r)}
            disabled={focused}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full font-bold text-sm border-2 transition-colors disabled:opacity-50 ${
              random ? "bg-[#10B981] text-white border-[#047857] shadow-[0_3px_0_#047857]" : "bg-white text-[#1E1B4B] border-blue-200 hover:bg-blue-50"
            }`}
            data-testid="listen-random-toggle"
            title={focused ? "Always shuffled in focused review" : "Toggle random / in-order"}
          >
            {random ? <Shuffle className="w-4 h-4" strokeWidth={3} /> : <ListOrdered className="w-4 h-4" strokeWidth={3} />}
            {random ? "Random" : "In order"}
          </button>
          <div className="chip" data-testid="listen-progress">
            Word {Math.min(idx + 1, queue.length)} of {queue.length}
          </div>
        </div>
      </div>

      <div className="h-3 w-full rounded-full bg-blue-100 overflow-hidden">
        <motion.div
          className="h-full bg-[#10B981]"
          animate={{ width: `${((idx + 1) / queue.length) * 100}%` }}
          transition={{ type: "spring", bounce: 0.2 }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${word}-${idx}`}
          initial={{ y: 30, opacity: 0, scale: 0.92 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: -30, opacity: 0, scale: 0.95 }}
          transition={{ type: "spring", bounce: 0.4 }}
          className="card-soft text-center py-10"
          data-testid="listen-card"
        >
          <Sparkles className="w-8 h-8 mx-auto text-[#FBBF24]" strokeWidth={3} />
          <div
            className="text-sm font-bold uppercase text-slate-500 mt-2"
            data-testid="listen-phase-label"
          >
            {phaseLabel}
          </div>

          <div className="my-8">
            {(() => {
              const letters = (word || "").toUpperCase().split("");
              const showingLetters = revealed || spellingIdx !== null;
              return (
                <div className="flex items-end justify-center gap-1 sm:gap-2 flex-wrap" data-testid="listen-word-letters">
                  {letters.map((L, i) => {
                    const isCurrent = spellingIdx === i;
                    const isPast = spellingIdx !== null && i < spellingIdx;
                    const visible = showingLetters && (spellingIdx === null || i <= spellingIdx);
                    const color = isCurrent
                      ? "text-[#FBBF24]"
                      : isPast
                        ? "text-[#10B981]"
                        : visible
                          ? "text-[#FF6B6B]"
                          : "text-slate-300";
                    return (
                      <motion.span
                        key={`${word}-${i}`}
                        initial={false}
                        animate={{ scale: isCurrent ? 1.25 : 1, y: isCurrent ? -6 : 0 }}
                        transition={{ type: "spring", stiffness: 500, damping: 18 }}
                        className={`font-display text-6xl sm:text-7xl font-bold ${color} tracking-wider inline-block`}
                      >
                        {visible ? L : "?"}
                      </motion.span>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          <div className="flex flex-wrap justify-center gap-2 mt-3">
            <button
              onClick={() => setRevealed((r) => !r)}
              className="btn-clay-ghost"
              data-testid="listen-reveal-btn"
            >
              {revealed ? <EyeOff className="w-5 h-5" strokeWidth={3} /> : <Eye className="w-5 h-5" strokeWidth={3} />}
              {revealed ? "Hide word" : "Show word"}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Sticky controls */}
      <div className="h-24 sm:h-4" aria-hidden="true" />
      <div
        className="sticky bottom-0 -mx-5 px-5 py-3 bg-[#F4F9FF]/90 backdrop-blur-md border-t-2 border-blue-100 z-30 flex items-center justify-between gap-3 flex-wrap"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
        data-testid="listen-nav-bar"
      >
        <div className="flex items-center gap-1.5" data-testid="listen-gap-picker">
          <span className="text-xs font-bold uppercase text-slate-500 mr-1">Gap</span>
          {GAP_OPTIONS.map((g) => (
            <button
              key={g.value}
              onClick={() => setGapMs(g.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${
                gapMs === g.value
                  ? "bg-[#4D96FF] text-white border-[#1D4ED8]"
                  : "bg-white text-[#1E1B4B] border-blue-200 hover:bg-blue-50"
              }`}
              data-testid={`listen-gap-${g.value}`}
            >
              {g.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={skipPrev} disabled={idx === 0} className="btn-clay-ghost disabled:opacity-40" data-testid="listen-prev-btn">
            <SkipBack className="w-5 h-5" strokeWidth={3} /> Prev
          </button>
          <button onClick={togglePause} className={paused ? "btn-clay-blue" : "btn-clay-yellow"} data-testid="listen-pause-btn">
            {paused ? <Play className="w-5 h-5" strokeWidth={3} /> : <Pause className="w-5 h-5" strokeWidth={3} />}
            {paused ? "Resume" : "Pause"}
          </button>
          <button onClick={skipNext} className="btn-clay-blue" data-testid="listen-next-btn">
            Next <SkipForward className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
      </div>
    </div>
  );
}
